import { useMemo } from 'react';
import { Users, Check, UserX, Eye, Crown, Trophy, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { UserAvatar } from '@/components/UserAvatar';

export interface RevealPick {
  user_id: string;
  display_name: string;
  avatar_id: number | null;
  label: string;
  points_earned?: number | null;
  is_correct_winner?: boolean | null;
  is_correct_score?: boolean | null;
}

export interface PicksRevealOutcome {
  winner: string;
  finalScore: string;        // display format "away-home"
  winningLabel: string;      // "TeamName in N"
}

interface PicksRevealSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  picks: RevealPick[];
  currentUserId: string | undefined;
  totalMembers: number;
  outcome?: PicksRevealOutcome;
}

type BucketHit = 'exact' | 'winner' | 'miss' | 'pending';

interface Bucket {
  label: string;
  picks: RevealPick[];
  hasUser: boolean;
  hit: BucketHit;
}

const AVATAR_CAP = 5;

const HIT_RANK: Record<BucketHit, number> = {
  exact: 0,
  winner: 1,
  pending: 2,
  miss: 3,
};

function classifyBucket(label: string, outcome?: PicksRevealOutcome): BucketHit {
  if (!outcome) return 'pending';
  if (label === outcome.winningLabel) return 'exact';
  // Parse "TeamName in N" — winner is everything before " in "
  const m = label.match(/^(.+) in \d+$/);
  const labelWinner = m ? m[1] : label;
  if (labelWinner === outcome.winner) return 'winner';
  return 'miss';
}

function buildBuckets(
  picks: RevealPick[],
  currentUserId: string | undefined,
  outcome?: PicksRevealOutcome,
): Bucket[] {
  const map = new Map<string, Bucket>();
  picks.forEach((p) => {
    const existing = map.get(p.label);
    if (existing) {
      existing.picks.push(p);
      if (p.user_id === currentUserId) existing.hasUser = true;
    } else {
      map.set(p.label, {
        label: p.label,
        picks: [p],
        hasUser: p.user_id === currentUserId,
        hit: classifyBucket(p.label, outcome),
      });
    }
  });
  const list = Array.from(map.values());
  // Sort: when outcome present → hit rank first then size; otherwise size only
  list.sort((a, b) => {
    if (outcome) {
      const r = HIT_RANK[a.hit] - HIT_RANK[b.hit];
      if (r !== 0) return r;
    }
    return b.picks.length - a.picks.length;
  });
  return list;
}

function bucketStyle(hit: BucketHit, hasUser: boolean): {
  ring: string;
  bar: string;
  labelColor: string;
  icon: JSX.Element | null;
} {
  if (hit === 'exact') {
    return {
      ring: 'border border-green-500/40 bg-green-500/5 ring-1 ring-green-500/20',
      bar: 'bg-green-400',
      labelColor: 'text-green-400',
      icon: <Crown className="w-3.5 h-3.5 shrink-0" />,
    };
  }
  if (hit === 'winner') {
    return {
      ring: 'border border-yellow-500/40 bg-yellow-500/5 ring-1 ring-yellow-500/20',
      bar: 'bg-yellow-400',
      labelColor: 'text-yellow-400',
      icon: <Trophy className="w-3.5 h-3.5 shrink-0" />,
    };
  }
  if (hit === 'miss') {
    return {
      ring: 'border border-border bg-transparent',
      bar: 'bg-muted-foreground/40',
      labelColor: 'text-muted-foreground',
      icon: <X className="w-3.5 h-3.5 shrink-0 opacity-50" />,
    };
  }
  // pending
  return {
    ring: hasUser ? 'border border-accent/30 bg-accent/5' : 'border border-border bg-transparent',
    bar: hasUser ? 'bg-accent' : 'bg-foreground/60',
    labelColor: hasUser ? 'text-accent' : 'text-foreground',
    icon: null,
  };
}

export function PicksRevealSheet({
  open,
  onClose,
  title,
  subtitle,
  picks,
  currentUserId,
  totalMembers,
  outcome,
}: PicksRevealSheetProps) {
  const buckets = useMemo(
    () => buildBuckets(picks, currentUserId, outcome),
    [picks, currentUserId, outcome],
  );
  const totalPicks = picks.length;
  const unpickedCount = Math.max(0, totalMembers - totalPicks);
  const userBucket = buckets.find((b) => b.hasUser);
  const largestCount = buckets.reduce((m, b) => Math.max(m, b.picks.length), 0);
  const exactBucket = buckets.find((b) => b.hit === 'exact');
  const winnerBuckets = buckets.filter((b) => b.hit === 'winner');

  const statusPill = (() => {
    if (!currentUserId) return null;
    if (!userBucket) {
      return {
        icon: <UserX className="w-4 h-4" />,
        text: 'לא הימרת על זה',
        tone: 'muted' as const,
      };
    }
    // Resolved: lead with hit-result tone
    if (outcome) {
      if (userBucket.hit === 'exact') {
        return {
          icon: <Crown className="w-4 h-4" />,
          text: 'פגעת בדיוק! 🎯',
          tone: 'ok' as const,
        };
      }
      if (userBucket.hit === 'winner') {
        return {
          icon: <Trophy className="w-4 h-4" />,
          text: 'ניצחת על המנצחת',
          tone: 'warn' as const,
        };
      }
      return {
        icon: <X className="w-4 h-4" />,
        text: 'ההימור הזה לא נכנס',
        tone: 'muted' as const,
      };
    }
    // Unresolved: lead with social tone
    const n = userBucket.picks.length;
    if (n === 1) {
      return {
        icon: <Eye className="w-4 h-4" />,
        text: 'אתה לבד על ההימור הזה',
        tone: 'warn' as const,
      };
    }
    if (n === largestCount) {
      return {
        icon: <Check className="w-4 h-4" />,
        text: `אתה עם הרוב (${n}/${totalMembers})`,
        tone: 'ok' as const,
      };
    }
    return {
      icon: <Users className="w-4 h-4" />,
      text: `אתה עם ${n - 1} ${n - 1 === 1 ? 'שחקן נוסף' : 'שחקנים'}`,
      tone: 'accent' as const,
    };
  })();

  const pillClasses: Record<'ok' | 'warn' | 'accent' | 'muted', string> = {
    ok: 'bg-green-500/10 text-green-400 border-green-500/30',
    warn: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    accent: 'bg-accent/10 text-accent border-accent/30',
    muted: 'bg-muted/30 text-muted-foreground border-border',
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="bottom"
        className="bg-card border-t border-border rounded-t-3xl max-h-[85vh] overflow-y-auto font-leon p-5"
        dir="rtl"
      >
        <SheetHeader className="text-right mb-4">
          <SheetTitle className="text-base font-bold tracking-tight pr-6">
            {title}
          </SheetTitle>
          {subtitle && (
            <p className="text-xs text-muted-foreground pr-6">{subtitle}</p>
          )}
        </SheetHeader>

        {/* Outcome banner — final result for resolved series */}
        {outcome && (
          <div className="mb-4 rounded-2xl border border-green-500/30 bg-gradient-to-l from-green-500/10 via-green-500/5 to-transparent p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-green-500/15 flex items-center justify-center shrink-0">
              <Crown className="w-5 h-5 text-green-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.15em] text-green-400/80 font-semibold">
                התוצאה הסופית
              </p>
              <p className="text-sm font-bold text-foreground truncate">
                {outcome.winner} ניצחה {outcome.finalScore}
              </p>
            </div>
            {exactBucket && (
              <div className="text-[11px] text-green-400 font-semibold whitespace-nowrap shrink-0">
                {exactBucket.picks.length} פגיעה מדויקת
              </div>
            )}
            {!exactBucket && winnerBuckets.length > 0 && (
              <div className="text-[11px] text-yellow-400 font-semibold whitespace-nowrap shrink-0">
                {winnerBuckets.reduce((s, b) => s + b.picks.length, 0)} כיוון נכון
              </div>
            )}
          </div>
        )}

        {statusPill && (
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-full border text-sm mb-5 w-fit ${pillClasses[statusPill.tone]}`}
          >
            {statusPill.icon}
            <span className="font-medium">{statusPill.text}</span>
          </div>
        )}

        {buckets.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">
            אף אחד לא הימר על זה
          </p>
        ) : (
          <div className="space-y-3">
            {buckets.map((bucket) => {
              const pct = totalPicks > 0 ? Math.round((bucket.picks.length / totalPicks) * 100) : 0;
              const visibleAvatars = bucket.picks.slice(0, AVATAR_CAP);
              const overflow = bucket.picks.length - visibleAvatars.length;
              const style = bucketStyle(bucket.hit, bucket.hasUser);
              return (
                <div
                  key={bucket.label}
                  className={`space-y-2 rounded-xl p-3 ${style.ring}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className={`text-sm font-semibold truncate flex items-center gap-1.5 ${style.labelColor}`}>
                      {style.icon}
                      {bucket.label}
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0" dir="ltr">
                      {bucket.picks.length}/{totalPicks} · {pct}%
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-muted/40 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${style.bar}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {visibleAvatars.map((p) => {
                      const showPoints =
                        outcome && typeof p.points_earned === 'number' && p.points_earned > 0;
                      return (
                        <div
                          key={p.user_id}
                          className="flex items-center gap-1.5 bg-muted/30 rounded-full pr-1 pl-2 py-0.5"
                          title={p.display_name}
                        >
                          <UserAvatar
                            avatarId={p.avatar_id}
                            displayName={p.display_name}
                            size="sm"
                            className="!h-5 !w-5"
                          />
                          <span className="text-[11px] text-muted-foreground truncate max-w-[80px]">
                            {p.display_name}
                          </span>
                          {showPoints && (
                            <span
                              className={`text-[10px] font-bold px-1.5 rounded-full whitespace-nowrap ${
                                bucket.hit === 'exact'
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-yellow-500/15 text-yellow-400'
                              }`}
                              dir="ltr"
                            >
                              +{p.points_earned}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {overflow > 0 && (
                      <span className="text-[11px] text-muted-foreground bg-muted/30 rounded-full px-2 py-1">
                        +{overflow}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {unpickedCount > 0 && (
              <div className="pt-3 border-t border-border">
                <span className="text-xs text-muted-foreground">
                  לא הימרו: {unpickedCount}
                </span>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default PicksRevealSheet;
